import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import { MergeElementProps } from '@/core/interfaces/common';
import { ThemeDirection } from '@/core/constants/constants';
import { paginationMessages } from '@/core/messages/pagination.messages';
import {
  ArrowLeft,
  ArrowRight,
} from '@/core/components/Icons';
import {
  Button,
  ButtonVariant,
} from '@/core/components/Button';
import { useGlobalContext } from '@/core/contexts/GlobalContext/GlobalContext';

import {
  PageNumber,
  PaginationButton,
  PaginationContainer,
  PaginationElementsContainer,
} from './Pagination.styles';
import { IconButtonSizes } from '../IconButton/IconButton.types';
import { IconButton } from '../IconButton';

const range = (start: number, end: number) => {
  const length = end - start + 1;

  return Array.from({ length }, (_, idx) => idx + start);
};

const DOTS = '...';

type PaginationProps = MergeElementProps<'div', {
  currentPage: number;
  total: number;
  extremePages?: number;
  pageSize: number;
  siblings?: number;
  totalPageCount: number;
  onPageChange(value: number): void;
}>

export const Pagination = ({
  currentPage,
  total,
  extremePages = 1,
  pageSize,
  siblings = 1,
  totalPageCount,
  onPageChange,
  ...props
}: PaginationProps) => {
  const theme = useTheme();

  const {
    isDesktop,
    isTabletOrAbove,
  } = useGlobalContext();

  const { formatMessage } = useIntl();

  const paginationRange = useMemo(() => {
    /**
     * siblings * 2 = two sides of siblings
     * extremePages * 2 = two sides of extreme pages
     * 3 = current page + both sides separators ("..." in default option)
     */
    const mediaRelatedSibling = isTabletOrAbove ? siblings : 0;
    const totalPageNumbers = mediaRelatedSibling * 2 + extremePages * 2 + 3;

    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    const leftExtreme = Array.from(Array(extremePages), (_, i) => i + 1);
    const rightExtreme = Array.from(
      Array(extremePages),
      (_, i) => totalPageCount - i
    ).reverse();

    const leftSiblingIndex = Math.max(currentPage - mediaRelatedSibling, 1);
    const rightSiblingIndex = Math.min(
      currentPage + mediaRelatedSibling,
      totalPageCount
    );

    const shouldShowLeftDots = leftSiblingIndex > extremePages + 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - extremePages - 1;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = extremePages + 2 * mediaRelatedSibling + 2;
      const leftRange = range(1, leftItemCount);

      return [
        ...leftRange,
        `${DOTS}_R`,
        ...rightExtreme,
      ];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = extremePages + 2 * mediaRelatedSibling + 1;
      const rightRange = range(
        totalPageCount - rightItemCount,
        totalPageCount
      );

      return [
        ...leftExtreme,
        `${DOTS}_L`,
        ...rightRange,
      ];
    }

    const middleRange = range(leftSiblingIndex, rightSiblingIndex);

    return [
      ...leftExtreme,
      `${DOTS}_L`,
      ...middleRange,
      `${DOTS}_R`,
      ...rightExtreme,
    ];
  }, [
    totalPageCount,
    siblings,
    currentPage,
    extremePages,
    isTabletOrAbove,
  ]);

  if (totalPageCount <= 1) {
    return null;
  }

  return (
    <PaginationContainer
      aria-label="Pagination container"
      {...props}
    >
      <>
        {(() => {
          const prevButtonCommonProps = {
            'aria-label': 'Previous page',
            isDisabled: currentPage <= 1,
            onClick: () => onPageChange(currentPage - 1),
          };

          return isTabletOrAbove ?
            (
              <Button
                startIcon={theme.direction === ThemeDirection.LTR ? <ArrowLeft /> : <ArrowRight />}
                variant={ButtonVariant.GHOST}
                label={isDesktop ? formatMessage(paginationMessages.previous) : ''}
                {...prevButtonCommonProps}
              />
            ) :
            (
              <IconButton
                icon={theme.direction === ThemeDirection.LTR ? <ArrowLeft /> : <ArrowRight />}
                variant={ButtonVariant.SECONDARY}
                size={IconButtonSizes.SMALL}
                ariaLabel="previous page"
                {...prevButtonCommonProps}
              />
            );
        })()}

        <PaginationElementsContainer>
          {paginationRange.map(pageNumber => {
            const separatorCommonProps = {
              'aria-label': 'Separator',
              key: pageNumber,
            };

            if (pageNumber === `${DOTS}_L` || pageNumber === `${DOTS}_R`) {
              return (
                <PageNumber {...separatorCommonProps}>
                  …
                </PageNumber>
              );
            }

            const currentButtonCommonProps = {
              'aria-label': `Page ${pageNumber}${currentPage === pageNumber ? ' - Current' : ''}`,
              ...(currentPage === pageNumber && { 'aria-current': true }),
              $isActive: currentPage === pageNumber,
              children: pageNumber,
              key: pageNumber,
              onClick: () => onPageChange(pageNumber as number),
              size: isTabletOrAbove ? IconButtonSizes.REGULAR : IconButtonSizes.SMALL,
            };

            return (
              <PaginationButton
                icon={(
                  <PageNumber>
                    {pageNumber}
                  </PageNumber>
                )}
                variant={ButtonVariant.SECONDARY}
                ariaLabel={`${pageNumber} page`}
                {...currentButtonCommonProps}
              />
            );
          })}
        </PaginationElementsContainer>

        {(() => {
          const nextButtonCommonProps = {
            'aria-label': 'Next page',
            isDisabled: currentPage >= totalPageCount,
            onClick: () => onPageChange(currentPage + 1),
          };

          return isTabletOrAbove ?
            (
              <Button
                endIcon={theme.direction === ThemeDirection.LTR ? <ArrowRight /> : <ArrowLeft />}
                variant={ButtonVariant.GHOST}
                label={isDesktop ? formatMessage(paginationMessages.next) : ''}
                {...nextButtonCommonProps}
              />
            ) :
            (
              <IconButton
                icon={theme.direction === ThemeDirection.LTR ? <ArrowRight /> : <ArrowLeft />}
                variant={ButtonVariant.SECONDARY}
                size={IconButtonSizes.SMALL}
                ariaLabel="next page"
                {...nextButtonCommonProps}
              />
            );
        })()}
      </>
    </PaginationContainer>
  );
};
